import { useState } from "react";

const AREAS = [
  {
    region: "서울",
    color: "bg-stone-900 text-white",
    dotColor: "bg-stone-900",
    districts: ["강남구", "서초구", "송파구", "강동구", "마포구", "용산구", "성동구", "광진구", "강서구", "양천구", "영등포구", "동작구", "관악구", "노원구", "도봉구", "강북구", "성북구", "종로구", "중구", "은평구", "서대문구", "중랑구", "동대문구", "구로구", "금천구"],
  },
  {
    region: "경기",
    color: "bg-stone-700 text-white",
    dotColor: "bg-stone-700",
    districts: ["의정부시", "수원시", "성남시", "고양시", "용인시", "부천시", "안산시", "안양시", "남양주시", "화성시", "평택시", "의왕시", "군포시", "광명시", "시흥시", "파주시", "김포시", "광주시", "양주시", "구리시", "오산시", "하남시", "이천시", "안성시"],
  },
  {
    region: "인천",
    color: "bg-stone-500 text-white",
    dotColor: "bg-stone-500",
    districts: ["중구", "동구", "미추홀구", "연수구", "남동구", "부평구", "계양구", "서구", "강화군", "옹진군"],
  },
];

const ServiceAreaSection = () => {
  const [activeRegion, setActiveRegion] = useState("서울");
  const active = AREAS.find((a) => a.region === activeRegion)!;

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-xs font-bold tracking-widest uppercase text-stone-400 mb-2">Service Area</p>
            <h2 className="text-3xl md:text-4xl font-black text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              서비스 지역
            </h2>
            <p className="text-sm text-stone-500 mt-2">서울 · 경기 · 인천 전 지역 당일 설치 가능합니다.</p>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <div className="w-2 h-2 rounded-full bg-emerald-500" style={{ animation: "pulse 2s infinite" }} />
            <span className="text-xs font-bold text-emerald-600 tracking-wide">지금 바로 예약 가능</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Left: Map */}
          <div className="rounded-2xl overflow-hidden border border-stone-100" style={{ height: "460px" }}>
            <iframe
              title="도어준 서비스 지역 지도"
              src="https://maps.google.com/maps?q=%EA%B2%BD%EA%B8%B0%EB%8F%84+%EC%9D%98%EC%A0%95%EB%B6%80%EC%8B%9C+%EC%9A%A9%ED%98%84%EB%A1%9C+118&t=m&z=13&output=embed&iwloc=near"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          {/* Right: Area Info */}
          <div className="flex flex-col gap-5">
            {/* Region Tabs */}
            <div className="flex items-center gap-2">
              {AREAS.map((area) => (
                <button
                  key={area.region}
                  onClick={() => setActiveRegion(area.region)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all whitespace-nowrap cursor-pointer ${
                    activeRegion === area.region
                      ? area.color
                      : "bg-stone-100 text-stone-500 hover:bg-stone-200"
                  }`}
                >
                  <div className={`w-1.5 h-1.5 rounded-full ${activeRegion === area.region ? "bg-white/70" : area.dotColor}`} />
                  {area.region}
                </button>
              ))}
            </div>

            {/* Districts */}
            <div className="bg-stone-50 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 flex items-center justify-center bg-stone-900 rounded-lg">
                  <i className="ri-map-pin-2-line text-white text-xs" />
                </div>
                <p className="text-sm font-black text-stone-900">{active.region} 전 지역</p>
                <span className="text-xs text-stone-400 ml-auto">{active.districts.length}개 지역</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {active.districts.map((d) => (
                  <span
                    key={d}
                    className="text-[11px] font-medium text-stone-600 bg-white border border-stone-200 rounded-full px-2.5 py-1"
                  >
                    {d}
                  </span>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: "ri-time-line", value: "당일", label: "설치 완료" },
                { icon: "ri-tools-line", value: "전문", label: "자격증 기사" },
                { icon: "ri-shield-check-line", value: "1년", label: "품질 보증" },
              ].map((s) => (
                <div key={s.label} className="bg-stone-50 rounded-xl p-4 text-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg mx-auto mb-2">
                    <i className={`${s.icon} text-stone-700 text-sm`} />
                  </div>
                  <p className="text-base font-black text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</p>
                  <p className="text-[10px] text-stone-500 mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="tel:01097939490"
                className="flex-1 flex items-center justify-center gap-2 bg-stone-900 text-white text-sm font-bold py-3.5 rounded-xl hover:bg-stone-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line" />
                010-9793-9490 전화 상담
              </a>
              <a
                href="https://open.kakao.com/o/srLtta1b"
                target="_blank"
                rel="nofollow noreferrer"
                className="flex-1 flex items-center justify-center gap-2 text-sm font-bold py-3.5 rounded-xl whitespace-nowrap cursor-pointer transition-colors"
                style={{ backgroundColor: "#FEE500", color: "#3A1D1D" }}
              >
                <i className="ri-chat-smile-2-line" />
                카카오 상담
              </a>
            </div>

            {/* Address */}
            <div className="flex items-start gap-3 bg-stone-50 rounded-xl px-4 py-3">
              <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg flex-shrink-0 mt-0.5">
                <i className="ri-map-pin-line text-stone-600 text-sm" />
              </div>
              <div>
                <p className="text-xs font-bold text-stone-700 mb-0.5">사업장 주소</p>
                <p className="text-xs text-stone-500">경기도 의정부시 용현로 118</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreaSection;
